import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';
import { Link } from '~/lib/react-router-dom';
import dayjs from '~/timeInit';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertColor,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type Theme, alpha } from '@mui/material/styles';

import AuditEventSummary from '~/components/Common/AuditEventSummary';
import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import SummaryToggleAccordion from '~/components/Common/Forms/SummaryToggleAccordion';
import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherCheck from '~/components/Vouchers/VoucherCheck';
import VoucherCloneDialog from '~/components/Vouchers/VoucherCloneDialog';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherLongExpiryPromoAdminAlert from '~/components/Vouchers/VoucherLongExpiryPromoAdminAlert';
import VoucherPreviewContent from '~/components/Vouchers/VoucherPreviewContent';
import VoucherRecentRequests from '~/components/Vouchers/VoucherRecentRequests';
import VoucherRewards from '~/components/Vouchers/VoucherRewards';
import VoucherTextReplacements from '~/components/Vouchers/VoucherTextReplacements';
import VoucherUsers from '~/components/Vouchers/VoucherUsers';
import { buildSuggestedClonedVoucherName } from '~/components/Vouchers/voucherCloneUtils';
import { serializeVoucherConditions } from '~/components/Vouchers/voucherConditionPayload';
import { VOUCHER_NAME_PATTERN_MESSAGE, validateRequiredVoucherName } from '~/components/Vouchers/voucherNameValidation';

import VoucherFormNavigation from '~/pages/Vouchers/VoucherFormNavigation';

import { toPromoBrand } from '~/consts/promoBrand';

import { useListLabAudiences } from '~/hooks/apis/lab/useListLabAudiences';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { useDeleteVoucher } from '~/hooks/apis/promo/useDeleteVoucher';
import { useGetVouchersByIds } from '~/hooks/apis/promo/useGetVouchersByIds';
import { useVoucherPreview } from '~/hooks/apis/promo/useVoucherPreview';
import { useGetUsersSummaryByIds } from '~/hooks/apis/users/useGetUsersSummaryByIds';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useDebounced from '~/hooks/useDebounced';

import type { LabAudienceListItem } from '~/services/LabService';
import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  PreviewVoucherPayload,
  PromoConditionTypeConfig,
  PromoMeta,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  VoucherUserExpiryApiPayload,
  createVoucher,
  getVoucher,
  syncVoucherLabAudience,
  updateVoucher,
} from '~/services/PromoService';
import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import ApiError from '~/types/ApiError';

import {
  getDefaultAddVoucherUsersExpiryDate,
  isoToSydneyDate,
  normalizeSydneyFormDateStringToIso,
  normalizeToISODate,
} from '~/utils/dateFormatter';
import { getDependentItemFieldsFromSelectedConditions } from '~/utils/promoRequestItemFieldDefaults';
import {
  FALLBACK_VOUCHER_REWARD_KIND_OPTIONS,
  type VoucherRewardKindOptionMeta,
  filterVoucherRewardKindOptionsForAdmin,
  voucherRewardKindOptionByKey,
} from '~/utils/voucherRewardKindMeta';

type RouteParams = {
  voucherId?: string;
};

type VoucherFormLocationState = { fromVoucherCreate?: { voucherName: string } } | null | undefined;

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];
type VoucherPresentationConfig = NonNullable<CreateVoucherPayload['voucher_presentation_config']>;
type VoucherToggleValue = string;
type VoucherToggleOption = {
  key: string;
  isActive: boolean;
  internalDescription: string;
  internalDescriptionLong: string;
  isBeingDeprecated?: boolean;
};
type VoucherWarning = {
  code?: string;
  message?: string;
  level?: string;
};

type VoucherApiFields = {
  voucherName?: string;
  voucherTitle?: string;
  voucherDescription?: string;
  voucherCallToAction?: string | null;
  voucherCallToActionUrl?: string | null;
  voucherPresentationConfig?: VoucherPresentationConfig | null;
  voucherBreakdownTitle?: string | null;
  voucherAmountDisplayOverride?: string | null;
  voucherWarnings?: Array<VoucherWarning>;
  currencyCode?: string;
  voucherRewards?: Array<VoucherReward>;
  voucherConditions?: Array<VoucherCondition>;
  voucherToggles?: Array<VoucherToggleValue>;
  showVoucherFromDate?: string;
  theme?: VoucherTheme;
  productType?: VoucherProductType;
  product_type?: VoucherProductType;
  voucher_name?: string;
  voucher_title?: string;
  voucher_description?: string;
  voucher_call_to_action?: string | null;
  voucher_call_to_action_url?: string | null;
  voucher_presentation_config?: VoucherPresentationConfig | null;
  voucher_breakdown_title?: string | null;
  voucher_amount_display_override?: string | null;
  currency_code?: string;
  voucher_rewards?: Array<VoucherReward>;
  voucher_conditions?: Array<VoucherCondition>;
  voucher_toggles?: Array<VoucherToggleValue>;
  show_voucher_from_date?: string;
};

type VoucherTheme = NonNullable<Voucher['theme']>;
type VoucherProductType = NonNullable<Voucher['productType']>;
type VoucherProductTypeMetaRow = PromoMeta['voucher_product_types'][number];
type VoucherThemeMetaRow = PromoMeta['voucher_themes'][number];
type VoucherRewardKind = Voucher['rewardKind'];

/** Only used when promo meta failed to load or returned an empty list. */
const FALLBACK_VOUCHER_PRODUCT_TYPE_KEYS: Array<VoucherProductType> = [
  'flight',
  'generic',
  'hotel',
  'cruise',
  'tour',
  'carhire',
];

const VOUCHER_USER_EXPIRY_NOT_SET_WARNING_CODE: VoucherWarning['code'] = 'VOUCHER_USER_EXPIRY_NOT_SET';

type VoucherUserExpiryMode = 'days' | 'date';

function voucherUserExpiryWarningFieldSx(theme: Theme) {
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.warning.main,
      borderWidth: 2,
    },
  };
}

type VoucherFormValues = {
  voucherName: string;
  voucherTitle: string;
  voucherDescription: string;
  /** Overrides the default call-to-action text shown with the voucher before redemption. */
  voucherCallToAction: string;
  /** Optional destination link for the voucher call-to-action. */
  voucherCallToActionUrl: string;
  /** Per-voucher controls for the presentation of voucher details and condition evaluation. */
  showCallToActionButtonInDetails: boolean;
  showConditionEvaluationWhenFailing: boolean;
  showConditionEvaluationWhenPassing: boolean;
  /** Overrides this voucher's label in the checkout discount breakdown (default "Voucher discount"). */
  voucherBreakdownTitle: string;
  voucherAmountDisplayOverride: string;
  /** Internal support reference; stored uppercased server-side. */
  caseId: string;
  /** Internal note (why the voucher exists). */
  reason: string;
  /** svc-lab audience id for Sync to Lab only (not persisted on the voucher). */
  labAudienceId: number | null;
  currencyCode: string;
  rewardKind: VoucherRewardKind;
  voucherRewards: Array<VoucherReward>;
  voucherConditions: Array<VoucherCondition>;
  voucherToggles: Array<VoucherToggleValue>;
  showVoucherFromDate: string;
  voucherUserExpiresAt: string;
  voucherUserExpiresInDays: string;
  theme: VoucherTheme;
  productType: VoucherProductType;
};

type VoucherPreviewPayloadBase = Omit<PreviewVoucherPayload, 'previewCurrencyCode' | 'previewExpiresAt'>;

const DEFAULT_REWARD_AMOUNT = 50;
const DEFAULT_MIN_SPEND = 500;
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = ['hotel-ALL', 'tour-ALL', 'cruise-ALL'];

function voucherUserExpiryToApiPayload(
  voucherUserExpiresAt: string,
  voucherUserExpiresInDays: string,
  mode: VoucherUserExpiryMode,
): VoucherUserExpiryApiPayload {
  if (mode === 'date') {
    const expiresAtTrimmed = voucherUserExpiresAt.trim();
    return {
      voucher_user_expires_at: expiresAtTrimmed ? normalizeToISODate(expiresAtTrimmed) : null,
      voucher_user_expires_in_days: null,
    };
  }
  const expiresInDaysTrimmed = voucherUserExpiresInDays.trim();
  let expiresInDaysNumber: number | undefined;
  if (expiresInDaysTrimmed !== '') {
    const parsed = Number.parseInt(expiresInDaysTrimmed, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      expiresInDaysNumber = parsed;
    }
  }
  return {
    voucher_user_expires_at: null,
    voucher_user_expires_in_days:
      expiresInDaysTrimmed === '' ? null : expiresInDaysNumber !== undefined ? expiresInDaysNumber : null,
  };
}

function getNormalizedVoucherRewards(data: VoucherFormValues, isEdit: boolean): Array<VoucherReward> {
  return data.voucherRewards.map((reward): VoucherReward => {
    const minSpendProducts = (reward.min_spend_products ?? []).filter(Boolean);
    const parsedMinSpendRaw =
      reward.min_spend == null || typeof reward.min_spend !== 'number' ? Number.NaN : Number(reward.min_spend);
    const parsedMinSpend = Number.isFinite(parsedMinSpendRaw) && parsedMinSpendRaw > 0 ? parsedMinSpendRaw : undefined;
    /** Only inject default min spend on create/preview — loaded vouchers already satisfy pairing when edited. */
    const minSpendForApi =
      minSpendProducts.length > 0 ? (parsedMinSpend ?? (isEdit ? undefined : DEFAULT_MIN_SPEND)) : parsedMinSpend;

    const { min_spend: _discardMinSpend, min_spend_products: _discardMinSpendProducts, ...rewardRest } = reward;

    return {
      ...rewardRest,
      currency_code: (reward.currency_code || data.currencyCode || 'AUD') as unknown as VoucherReward['currency_code'],
      reward_amount: Number(reward.reward_amount),
      min_spend_products: minSpendProducts,
      ...(minSpendForApi !== undefined ? { min_spend: minSpendForApi } : {}),
    };
  });
}

function getExistingVoucherConditionIds(voucher: Voucher | null): ReadonlySet<string> {
  if (!voucher) {
    return new Set();
  }

  const data = voucher as VoucherApiFields;
  return new Set(
    (data.voucherConditions ?? data.voucher_conditions ?? []).flatMap((condition) =>
      condition.id_voucher_condition ? [condition.id_voucher_condition] : [],
    ),
  );
}

function getShowVoucherFromDateValue(data: VoucherFormValues, includeTime: boolean): string {
  const rawShowFrom = (data.showVoucherFromDate || '').trim();
  if (!rawShowFrom) {
    return '';
  }
  if (includeTime) {
    return normalizeSydneyFormDateStringToIso(data.showVoucherFromDate);
  }
  return normalizeToISODate(rawShowFrom.length === 10 ? rawShowFrom : rawShowFrom.slice(0, 10) || rawShowFrom);
}

function buildVoucherPreviewPayloadBase(params: {
  data: VoucherFormValues;
  brand: CreateVoucherPayload['brand'];
  existingVoucherConditionIds: ReadonlySet<string>;
  isEdit: boolean;
  showVoucherFromDateIncludeTime: boolean;
  voucherUserExpiryMode: VoucherUserExpiryMode;
}): VoucherPreviewPayloadBase | null {
  const { data, brand, existingVoucherConditionIds, isEdit, showVoucherFromDateIncludeTime, voucherUserExpiryMode } =
    params;
  if (!isEdit && getVoucherPreviewMissingFieldMessages(data, isEdit).length > 0) {
    return null;
  }
  const showVoucherFromDateValue = getShowVoucherFromDateValue(data, showVoucherFromDateIncludeTime);
  const { voucher_user_expires_at, voucher_user_expires_in_days } = voucherUserExpiryToApiPayload(
    data.voucherUserExpiresAt,
    data.voucherUserExpiresInDays,
    voucherUserExpiryMode,
  );

  return {
    voucher_name: data.voucherName.trim(),
    brand,
    voucher_title: data.voucherTitle,
    voucher_description: data.voucherDescription,
    ...(data.voucherCallToAction.trim() ? { voucher_call_to_action: data.voucherCallToAction.trim() } : {}),
    ...(data.voucherCallToActionUrl.trim() ? { voucher_call_to_action_url: data.voucherCallToActionUrl.trim() } : {}),
    voucher_presentation_config: {
      showCallToActionButtonInDetails: data.showCallToActionButtonInDetails,
      showConditionEvaluationWhenFailing: data.showConditionEvaluationWhenFailing,
      showConditionEvaluationWhenPassing: data.showConditionEvaluationWhenPassing,
    },
    ...(data.voucherBreakdownTitle.trim() ? { voucher_breakdown_title: data.voucherBreakdownTitle.trim() } : {}),
    ...(data.voucherAmountDisplayOverride.trim()
      ? { voucher_amount_display_override: data.voucherAmountDisplayOverride.trim() }
      : {}),
    voucher_rewards: getNormalizedVoucherRewards(data, isEdit) as PreviewVoucherPayload['voucher_rewards'],
    voucher_conditions: serializeVoucherConditions(
      data.voucherConditions,
      existingVoucherConditionIds,
    ) as PreviewVoucherPayload['voucher_conditions'],
    voucher_toggles: data.voucherToggles as PreviewVoucherPayload['voucher_toggles'],
    ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
    voucher_user_expires_at: voucher_user_expires_at ?? undefined,
    voucher_user_expires_in_days: voucher_user_expires_in_days ?? undefined,
    theme: data.theme as PreviewVoucherPayload['theme'],
    product_type: data.productType as PreviewVoucherPayload['product_type'],
    ...(data.caseId.trim() ? { case_id: data.caseId.trim().toUpperCase() } : {}),
    ...(data.reason.trim() ? { reason: data.reason.trim() } : {}),
    reward_kind: data.rewardKind as PreviewVoucherPayload['reward_kind'],
  };
}

function getVoucherPreviewMissingFieldMessages(data: VoucherFormValues, isEdit: boolean): Array<string> {
  if (isEdit) {
    return [];
  }

  const voucherNameValidation = validateVoucherName(data.voucherName);
  return voucherNameValidation === true ? [] : [`Voucher name: ${voucherNameValidation}`];
}

function validateVoucherName(value: string): true | string {
  return validateRequiredVoucherName(value);
}

const buildDefaultFormValues = (): VoucherFormValues => ({
  voucherName: '',
  voucherTitle: '',
  voucherDescription: '',
  voucherCallToAction: '',
  voucherCallToActionUrl: '',
  showCallToActionButtonInDetails: true,
  showConditionEvaluationWhenFailing: false,
  showConditionEvaluationWhenPassing: false,
  voucherBreakdownTitle: '',
  voucherAmountDisplayOverride: '',
  caseId: '',
  reason: '',
  labAudienceId: null,
  currencyCode: 'AUD',
  rewardKind: 'discount',
  voucherRewards: [
    {
      id_voucher_reward: uuid(),
      reward_amount: DEFAULT_REWARD_AMOUNT,
      products: DEFAULT_REWARD_PRODUCTS,
      min_spend: DEFAULT_MIN_SPEND,
      min_spend_products: [...DEFAULT_REWARD_PRODUCTS] as NonNullable<VoucherReward['min_spend_products']>,
    },
  ],
  voucherConditions: [],
  voucherToggles: [],
  showVoucherFromDate: '',
  voucherUserExpiresAt: '',
  voucherUserExpiresInDays: '',
  theme: 'default',
  productType: 'generic',
});

/**
 * True when the API value is only a calendar start date (no specific time of day):
 * a bare YYYY-MM-DD, Sydney start-of-day, or the legacy start-of-day-UTC serialization.
 */
function isStartDateOnlyInApi(s: string): boolean {
  const t = s.trim();
  if (!t) {
    return true;
  }
  if (!t.includes('T')) {
    return true;
  }
  if (/T00:00:00(\.0+)?Z$/.test(t) || /T00:00:00(\.0+)?\+00:00$/.test(t)) {
    return true;
  }
  const d = dayjs(t);
  return d.isValid() && d.tz(SYDNEY_TIMEZONE).format('HH:mm:ss.SSS') === '00:00:00.000';
}

/** Maps API "show from" to form: date field by default; date+time if a specific instant is stored. */
function getShowVoucherFromFieldsFromApi(raw: string | undefined | null): { value: string; includeTime: boolean } {
  if (!raw?.toString().trim()) {
    return { value: '', includeTime: false };
  }
  const s = raw.toString().trim();
  if (isStartDateOnlyInApi(s)) {
    return {
      // Legacy UTC-midnight values keep their stored calendar day; Sydney-midnight values map back via tz.
      value: /T00:00:00(\.0+)?(Z|\+00:00)$/.test(s) ? s.slice(0, 10) : isoToSydneyDate(s),
      includeTime: false,
    };
  }
  const d = dayjs(s);
  return {
    value: d.isValid() ? d.tz(SYDNEY_TIMEZONE).format('YYYY-MM-DDTHH:mm') : '',
    includeTime: true,
  };
}

const parseVoucherApiResponse = (voucher: Voucher): VoucherFormValues => {
  const data = voucher as VoucherApiFields;
  const raw = voucher as unknown as Record<string, unknown>;
  const rawCurrencyCode = data.currencyCode || data.currency_code;
  const rawExpiresInDays = raw.voucherUserExpiresInDays ?? raw.voucher_user_expires_in_days;
  const voucherWithCase = voucher as Voucher & { case_id?: string };
  const caseFromApi = voucherWithCase.caseId ?? voucherWithCase.case_id;
  const voucherRewardKindFields = voucher as Voucher & { reward_kind?: string };
  const rewardKindRaw = voucherRewardKindFields.rewardKind ?? voucherRewardKindFields.reward_kind;
  const rewardKindTrimmed =
    rewardKindRaw != null && String(rewardKindRaw).trim() !== '' ? String(rewardKindRaw).trim() : '';
  const rewardKind: VoucherRewardKind = (
    rewardKindTrimmed !== '' ? rewardKindTrimmed : 'discount'
  ) as VoucherRewardKind;
  const voucherPresentationConfig = data.voucherPresentationConfig ?? data.voucher_presentation_config;
  return {
    voucherName: data.voucherName || data.voucher_name || '',
    voucherTitle: data.voucherTitle || data.voucher_title || '',
    voucherDescription: data.voucherDescription || data.voucher_description || '',
    voucherCallToAction: data.voucherCallToAction || data.voucher_call_to_action || '',
    voucherCallToActionUrl: data.voucherCallToActionUrl || data.voucher_call_to_action_url || '',
    showCallToActionButtonInDetails: voucherPresentationConfig?.showCallToActionButtonInDetails ?? true,
    showConditionEvaluationWhenFailing: voucherPresentationConfig?.showConditionEvaluationWhenFailing ?? false,
    showConditionEvaluationWhenPassing: voucherPresentationConfig?.showConditionEvaluationWhenPassing ?? false,
    voucherBreakdownTitle: data.voucherBreakdownTitle || data.voucher_breakdown_title || '',
    voucherAmountDisplayOverride: String(
      data.voucherAmountDisplayOverride ?? data.voucher_amount_display_override ?? '',
    ),
    caseId: caseFromApi != null && String(caseFromApi).trim() !== '' ? String(caseFromApi) : '',
    reason: voucher.reason != null && String(voucher.reason).trim() !== '' ? String(voucher.reason) : '',
    labAudienceId: null,
    currencyCode: rawCurrencyCode && currencyCodes.includes(rawCurrencyCode) ? rawCurrencyCode : 'AUD',
    rewardKind,
    voucherRewards: (data.voucherRewards ?? data.voucher_rewards ?? []).map((reward) => ({
      ...reward,
      id_voucher_reward: reward.id_voucher_reward || uuid(),
    })),
    voucherConditions: (data.voucherConditions ?? data.voucher_conditions ?? []).map((condition) => ({
      ...condition,
      id_voucher_condition: condition.id_voucher_condition || uuid(),
    })),
    voucherToggles: data.voucherToggles ?? data.voucher_toggles ?? [],
    showVoucherFromDate: getShowVoucherFromFieldsFromApi(data.showVoucherFromDate || data.show_voucher_from_date).value,
    voucherUserExpiresAt: isoToSydneyDate(String(raw.voucherUserExpiresAt ?? raw.voucher_user_expires_at ?? '')),
    voucherUserExpiresInDays: rawExpiresInDays != null && rawExpiresInDays !== '' ? String(rawExpiresInDays) : '',
    theme: data.theme || 'default',
    productType: data.productType || data.product_type || 'generic',
  };
};

const getVoucherWarningSeverity = (level?: string): AlertColor => {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
};

/** `request()` maps JSON error bodies onto `ApiError` (`message`, `status`, `errors`). */
function formatVoucherSaveFailure(error: unknown): string {
  if (error instanceof ApiError) {
    const parts: Array<string> = [error.message];
    if (error.status != null) {
      parts.push(`HTTP ${error.status}`);
    }
    const { errors } = error;
    let bodyDetails: string | null = null;
    if (errors !== undefined && errors !== null) {
      if (typeof errors === 'string') {
        const trimmed = errors.trim();
        bodyDetails = trimmed.length > 0 ? trimmed : null;
      } else if (Array.isArray(errors)) {
        bodyDetails = errors.length > 0 ? JSON.stringify(errors, null, 2) : null;
      } else {
        const serialized = JSON.stringify(errors, null, 2);
        bodyDetails = serialized !== '{}' && serialized !== 'null' ? serialized : null;
      }
    }
    if (bodyDetails) {
      parts.push(bodyDetails);
    }
    return parts.join(' — ');
  }
  if (error instanceof Error) {
    return error.message;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const isEdit = Boolean(voucherId);
  const { block, push, replace } = useAppNavigation();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, formState, watch, setValue, getValues, trigger } = useForm<VoucherFormValues>({
    defaultValues: buildDefaultFormValues(),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const { isDirty, isSubmitting, dirtyFields } = formState;

  /** When true, the next in-app navigation is allowed without the unsaved-changes confirm (e.g. after delete). */
  const bypassUnsavedNavigationBlockRef = useRef(false);
  /** Values loaded from API (edit mode) — used to warn before clearing case ID / reason. */
  const initialInternalFieldsRef = useRef<{ caseId: string; reason: string }>({ caseId: '', reason: '' });
  const watchedVoucherUserExpiresAt = watch('voucherUserExpiresAt');
  const watchedVoucherUserExpiresInDays = watch('voucherUserExpiresInDays');
  const watchedVoucherDescription = watch('voucherDescription');
  const watchedLabAudienceId = watch('labAudienceId');

  const [showAdvanceDescription, setShowAdvanceDescription] = useState(false);
  /** When true, the show-from field includes a time; when false, start date only (the usual case). */
  const [showVoucherFromDateIncludeTime, setShowVoucherFromDateIncludeTime] = useState(false);
  const [voucherUsers, setVoucherUsers] = useState<Array<string>>([]);
  /** User ID filter shared between voucher users grid and Recent Requests (voucher edit page only). */
  const [promoRequestsUserFilter, setPromoRequestsUserFilter] = useState('');
  const [expiredAt, setExpiredAt] = useState('');
  const [voucherUserExpiryMode, setVoucherUserExpiryMode] = useState<VoucherUserExpiryMode>('days');
  /** Must stay in sync with `voucherUserExpiryMode` before paint (e.g. RHF `setValue` + `shouldValidate`). */
  const voucherUserExpiryModeRef = useRef<VoucherUserExpiryMode>('days');
  const [descriptionAccordionExpanded, setDescriptionAccordionExpanded] = useState(false);
  const [previewAccordionExpanded, setPreviewAccordionExpanded] = useState(false);
  const lastAutoPreviewKeyRef = useRef('');
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
  const [labAudienceSearch, setLabAudienceSearch] = useState('');
  const debouncedLabAudienceSearch = useDebounced(labAudienceSearch, 300);
  const hasUnsavedRewardChanges = Boolean(dirtyFields.voucherRewards);
  const hasUnsavedConditionChanges = Boolean(dirtyFields.voucherConditions);
  const currentBrand = useCurrentBrand();
  let brand = toPromoBrand(currentBrand);
  if (!brand) {
    console.error(`could not load promo brand ${currentBrand}. default to luxuryescapes`);
    brand = 'luxuryescapes';
  }
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const { data: voucherResponse, isLoading: isVoucherLoading } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);
  const existingVoucherConditionIds = useMemo(() => getExistingVoucherConditionIds(voucher), [voucher]);
  const voucherDescriptionSummaryPreview = useMemo(() => {
    const text = watchedVoucherDescription?.trim() ?? '';
    if (!text) {
      return 'Optional — supports markdown';
    }
    const singleLine = text.replace(/\s+/g, ' ').trim();
    const maxLength = 120;
    return singleLine.length > maxLength ? `${singleLine.slice(0, maxLength)}…` : singleLine;
  }, [watchedVoucherDescription]);

  useEffect(() => {
    if (voucher) {
      const v = voucher as Voucher & { case_id?: string };
      initialInternalFieldsRef.current = {
        caseId: String(v.caseId ?? v.case_id ?? '').trim(),
        reason: String(v.reason ?? '').trim(),
      };
    } else if (!isEdit) {
      initialInternalFieldsRef.current = { caseId: '', reason: '' };
    }
  }, [voucher, isEdit]);

  const parentVoucherId = useMemo(() => {
    if (!voucher) {
      return '';
    }
    const raw = (voucher as Voucher & { clonedFromId?: string | null }).clonedFromId;
    return typeof raw === 'string' ? raw.trim() : '';
  }, [voucher]);

  const { data: parentVouchersMap } = useGetVouchersByIds({
    brand: brand,
    voucherIds: parentVoucherId ? [parentVoucherId] : [],
    enabled: Boolean(parentVoucherId),
  });
  const parentVoucherName = parentVoucherId ? parentVouchersMap?.get(parentVoucherId)?.voucherName?.trim() : undefined;

  const voucherAuditFields = useMemo(() => {
    if (!voucher) {
      return {
        createdByUserId: '',
        modifiedByUserId: '',
        createdAt: '',
        updatedAt: '',
      };
    }
    const v = voucher as Voucher & {
      created_by?: string;
      updated_by?: string;
      modified_by?: string;
      created_at?: string;
      updated_at?: string;
    };
    return {
      createdByUserId: (v.createdBy ?? v.created_by ?? '').trim(),
      modifiedByUserId: (v.updatedBy ?? v.updated_by ?? v.modified_by ?? '').trim(),
      createdAt: v.createdAt ?? v.created_at ?? '',
      updatedAt: v.updatedAt ?? v.updated_at ?? '',
    };
  }, [voucher]);

  const voucherAuditUserIds = useMemo(
    () => [voucherAuditFields.createdByUserId, voucherAuditFields.modifiedByUserId].filter(Boolean),
    [voucherAuditFields.createdByUserId, voucherAuditFields.modifiedByUserId],
  );

  const { data: voucherAuditUsersById = new Map<string, App.UserSummary>() } = useGetUsersSummaryByIds({
    userIds: voucherAuditUserIds,
    brand,
    enabled: Boolean(isEdit && voucher && voucherAuditUserIds.length > 0),
  });

  const voucherWarnings = useMemo(() => {
    if (!voucher) {
      return [];
    }
    const voucherData = voucher as VoucherApiFields;
    return voucherData.voucherWarnings ?? [];
  }, [voucher]);

  const highlightVoucherUserExpiryFields = useMemo(
    () => voucherWarnings.some((w) => w.code === VOUCHER_USER_EXPIRY_NOT_SET_WARNING_CODE),
    [voucherWarnings],
  );

  // Populate form from API data — guarded to run only once per voucherId
  useEffect(() => {
    if (!isEdit) {
      const defaults = buildDefaultFormValues();
      reset(defaults);
      setVoucherUsers([]);
      setPromoRequestsUserFilter('');
      setExpiredAt('');
      voucherUserExpiryModeRef.current = 'days';
      setVoucherUserExpiryMode('days');
      setShowVoucherFromDateIncludeTime(false);
      setDescriptionEditorKey((prev) => prev + 1);
      return;
    }
    if (voucher) {
      const data = voucher as VoucherApiFields;
      const raw = data.showVoucherFromDate || data.show_voucher_from_date;
      const { includeTime } = getShowVoucherFromFieldsFromApi(raw);
      const values = parseVoucherApiResponse(voucher);
      reset(values);
      setVoucherUsers([]);
      setPromoRequestsUserFilter('');
      setExpiredAt('');
      const nextExpiryMode: VoucherUserExpiryMode = values.voucherUserExpiresAt.trim() ? 'date' : 'days';
      voucherUserExpiryModeRef.current = nextExpiryMode;
      setVoucherUserExpiryMode(nextExpiryMode);
      setShowVoucherFromDateIncludeTime(includeTime);
      setDescriptionEditorKey((prev) => prev + 1);
    }
  }, [isEdit, voucher, voucherId, reset]);

  const voucherNameValue = watch('voucherName');
  const watchedVoucherTitle = watch('voucherTitle');
  const watchedVoucherAmountDisplayOverride = watch('voucherAmountDisplayOverride');
  const watchedProductType = watch('productType');
  const watchedShowVoucherFromDate = watch('showVoucherFromDate');
  const voucherToggles = watch('voucherToggles');
  const voucherRewards = watch('voucherRewards');
  const voucherConditions = watch('voucherConditions');
  const currencyCode = watch('currencyCode');
  const watchedRewardKind = watch('rewardKind');
  const watchedTheme = watch('theme');

  const voucherToggleOptions: Array<VoucherToggleOption> = promoMeta?.voucher_toggles ?? [];

  const voucherPreviewCurrencyOptions = useMemo(() => {
    const fallbackCurrency = currencyCode || 'AUD';
    return voucherRewards.map((reward) => reward.currency_code || fallbackCurrency).filter(Boolean);
  }, [currencyCode, voucherRewards]);

  const buildPreviewPayload = useCallback(
    () =>
      buildVoucherPreviewPayloadBase({
        data: getValues(),
        brand,
        existingVoucherConditionIds,
        isEdit,
        showVoucherFromDateIncludeTime,
        voucherUserExpiryMode,
      }),
    [brand, existingVoucherConditionIds, getValues, isEdit, showVoucherFromDateIncludeTime, voucherUserExpiryMode],
  );

  const getPreviewMissingFieldMessages = useCallback(
    () => getVoucherPreviewMissingFieldMessages(getValues(), isEdit),
    [getValues, isEdit],
  );

  const {
    handlePreview: handleRefreshPreview,
    isPreviewError,
    isPreviewPending,
    previewCurrencyCode,
    previewErrorDetailBlock,
    previewErrorHeadline,
    previewLocalError,
    previewTextReplacements,
    publicPreviewVoucher,
    resolvedCurrencyOptions: resolvedVoucherPreviewCurrencyOptions,
    setPreviewCurrencyCode,
  } = useVoucherPreview({
    buildPayload: buildPreviewPayload,
    currencyOptions: voucherPreviewCurrencyOptions,
    defaultCurrencyCode: voucherPreviewCurrencyOptions[0] || currencyCode || 'AUD',
    getMissingFieldMessages: getPreviewMissingFieldMessages,
  });

  const autoPreviewRefreshKey = useMemo(
    () =>
      JSON.stringify({
        voucherId: voucherId || 'create',
        currencyCode: previewCurrencyCode,
        title: watchedVoucherTitle,
        amountDisplayOverride: watchedVoucherAmountDisplayOverride,
        rewardKind: watchedRewardKind,
        rewards: voucherRewards.map((reward) => ({
          currencyCode: reward.currency_code,
          minSpend: reward.min_spend,
          minSpendProducts: reward.min_spend_products,
          products: reward.products,
          rewardAmount: reward.reward_amount,
        })),
        showVoucherFromDate: watchedShowVoucherFromDate,
        showVoucherFromDateIncludeTime,
        voucherUserExpiresAt: watchedVoucherUserExpiresAt,
        voucherUserExpiresInDays: watchedVoucherUserExpiresInDays,
        voucherUserExpiryMode,
      }),
    [
      previewCurrencyCode,
      showVoucherFromDateIncludeTime,
      voucherId,
      voucherRewards,
      voucherUserExpiryMode,
      watchedRewardKind,
      watchedShowVoucherFromDate,
      watchedVoucherAmountDisplayOverride,
      watchedVoucherTitle,
      watchedVoucherUserExpiresAt,
      watchedVoucherUserExpiresInDays,
    ],
  );

  const defaultVisibleRequestItemFields = useMemo(
    () =>
      getDependentItemFieldsFromSelectedConditions(
        voucherConditions,
        (promoMeta?.voucher_condition_types as Array<PromoConditionTypeConfig> | undefined) ?? [],
      ),
    [promoMeta?.voucher_condition_types, voucherConditions],
  );
  const voucherThemeMenuRows: Array<VoucherThemeMetaRow> = useMemo(() => {
    if (isPromoMetaLoading) {
      return [];
    }
    const fromMeta = promoMeta?.voucher_themes;
    if (fromMeta && fromMeta.length > 0) {
      const keys = new Set<VoucherTheme>(fromMeta.map((row) => row.key as VoucherTheme));
      if (!keys.has(watchedTheme) && isEdit) {
        return [
          ...fromMeta,
          {
            key: watchedTheme,
            name: `[UNKNOWN] ${watchedTheme}`,
            description: 'This theme is not in current voucher metadata and will be preserved unless you change it.',
          },
        ];
      }
      return fromMeta;
    }
    if (isEdit && watchedTheme) {
      return [
        {
          key: watchedTheme,
          name: `[UNKNOWN] ${watchedTheme}`,
          description:
            'Theme options are not available from promo meta. Current value is shown until a list is available.',
        },
      ];
    }
    return [];
  }, [promoMeta, isPromoMetaLoading, watchedTheme, isEdit]);

  const voucherProductTypeMenuRows: Array<VoucherProductTypeMetaRow> = useMemo(() => {
    const fromMeta = promoMeta?.voucher_product_types;
    if (fromMeta && fromMeta.length > 0) {
      const keys = new Set<VoucherProductType>(fromMeta.map((row) => row.key as VoucherProductType));
      if (!keys.has(watchedProductType) && isEdit) {
        return [
          ...fromMeta,
          {
            key: watchedProductType,
            name: `[UNKNOWN] ${watchedProductType}`,
            description:
              'This product type is not in current voucher metadata and will be preserved unless you change it.',
          },
        ];
      }
      return fromMeta;
    }
    if (isPromoMetaLoading) {
      return [];
    }
    return FALLBACK_VOUCHER_PRODUCT_TYPE_KEYS.map((key) => ({
      key,
      name: key,
      description: '',
    }));
  }, [promoMeta, isPromoMetaLoading, watchedProductType, isEdit]);

  const voucherRewardKindMenuRows: Array<VoucherRewardKindOptionMeta> = useMemo(() => {
    const fullByKey = voucherRewardKindOptionByKey(promoMeta);
    const fromMeta = filterVoucherRewardKindOptionsForAdmin(promoMeta);
    const keys = new Set(fromMeta.map((row) => row.key));
    if (watchedRewardKind && !keys.has(watchedRewardKind) && isEdit) {
      const fromCatalog = fullByKey.get(watchedRewardKind);
      return [
        ...fromMeta,
        fromCatalog ?? {
          key: watchedRewardKind,
          name: `[UNKNOWN] ${watchedRewardKind}`,
          description: 'This reward kind is not in current promo metadata and will be preserved unless you change it.',
          showInAdmin: true,
        },
      ];
    }
    if (fromMeta.length > 0) {
      return fromMeta;
    }
    if (isPromoMetaLoading) {
      return [];
    }
    return FALLBACK_VOUCHER_REWARD_KIND_OPTIONS;
  }, [promoMeta, isPromoMetaLoading, watchedRewardKind, isEdit]);

  const selectedUnknownVoucherToggleOptions = useMemo<Array<VoucherToggleOption>>(() => {
    const knownToggleKeys = new Set(voucherToggleOptions.map((option) => option.key));
    return voucherToggles
      .filter((key) => !knownToggleKeys.has(key))
      .map((key) => ({
        key,
        isActive: false,
        internalDescription: `[UNKNOWN] ${key}`,
        internalDescriptionLong:
          'This toggle key is not in current voucher metadata and will be preserved unless explicitly removed.',
      }));
  }, [voucherToggles, voucherToggleOptions]);

  const voucherToggleOptionsWithUnknown = useMemo<Array<VoucherToggleOption>>(() => {
    const allOptions = [...voucherToggleOptions, ...selectedUnknownVoucherToggleOptions];
    return allOptions.sort((a, b) => {
      if (a.isBeingDeprecated !== b.isBeingDeprecated) {
        return Number(a.isBeingDeprecated ?? false) - Number(b.isBeingDeprecated ?? false);
      }
      return a.internalDescription.localeCompare(b.internalDescription);
    });
  }, [voucherToggleOptions, selectedUnknownVoucherToggleOptions]);

  useEffect(() => {
    if (voucherUserExpiryModeRef.current === 'date') {
      void trigger('voucherUserExpiresAt');
    }
  }, [watchedShowVoucherFromDate, trigger]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const unblock = block(() => {
      if (bypassUnsavedNavigationBlockRef.current) {
        bypassUnsavedNavigationBlockRef.current = false;
        return undefined;
      }
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [isDirty, block]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'users') {
      const usersElement = document.getElementById('users');
      if (usersElement) {
        usersElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.hash]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
  });

  const syncLabAudienceMutation = useMutation({
    mutationFn: async ({ id, labAudienceId }: { id: string; labAudienceId: number }) =>
      syncVoucherLabAudience(id, labAudienceId),
    onSuccess: (response) => {
      enqueueSnackbar(`Synced ${response.result.membersSynced} members to Lab`, { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(formatVoucherSaveFailure(error), { variant: 'error' });
    },
  });

  const labAudienceListQuery = useMemo(
    () => ({
      page: 1,
      ...(debouncedLabAudienceSearch.trim() ? { search: debouncedLabAudienceSearch.trim() } : {}),
    }),
    [debouncedLabAudienceSearch],
  );

  const { data: labAudiencesResponse, isFetching: isLabAudiencesFetching } = useListLabAudiences({
    query: labAudienceListQuery,
  });

  const labAudienceOptions = useMemo((): Array<LabAudienceListItem> => {
    const audiences = labAudiencesResponse?.data?.audiences ?? [];
    if (watchedLabAudienceId == null) {
      return audiences;
    }
    if (audiences.some((audience) => audience.id === watchedLabAudienceId)) {
      return audiences;
    }
    return [{ id: watchedLabAudienceId, name: `Audience #${watchedLabAudienceId} (unavailable)` }, ...audiences];
  }, [labAudiencesResponse?.data?.audiences, watchedLabAudienceId]);

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const deleteVoucherMutation = useDeleteVoucher();

  useEffect(() => {
    if (isEdit && (isVoucherLoading || !voucher)) {
      return undefined;
    }
    if (!previewCurrencyCode) {
      return undefined;
    }
    if (lastAutoPreviewKeyRef.current === autoPreviewRefreshKey) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      lastAutoPreviewKeyRef.current = autoPreviewRefreshKey;
      void handleRefreshPreview();
    }, 450);
    return () => window.clearTimeout(timeoutId);
  }, [autoPreviewRefreshKey, handleRefreshPreview, isEdit, isVoucherLoading, previewCurrencyCode, voucher]);

  const onSubmit = async (data: VoucherFormValues) => {
    try {
      if (isEdit) {
        const initial = initialInternalFieldsRef.current;
        const clearingCaseId = initial.caseId !== '' && data.caseId.trim() === '';
        const clearingReason = initial.reason !== '' && data.reason.trim() === '';
        if (clearingCaseId || clearingReason) {
          const clearedLabels: Array<string> = [];
          if (clearingCaseId) {
            clearedLabels.push('case ID');
          }
          if (clearingReason) {
            clearedLabels.push('reason');
          }
          const proceed = window.confirm(
            `You are about to clear the voucher ${clearedLabels.join(' and ')}. Continue?`,
          );
          if (!proceed) {
            return;
          }
        }
      }

      const normalizedRewards = getNormalizedVoucherRewards(data, isEdit);
      let savedVoucherId = voucherId || '';
      const showVoucherFromDateValue = getShowVoucherFromDateValue(data, showVoucherFromDateIncludeTime);
      const { voucher_user_expires_at, voucher_user_expires_in_days } = voucherUserExpiryToApiPayload(
        data.voucherUserExpiresAt,
        data.voucherUserExpiresInDays,
        voucherUserExpiryMode,
      );

      if (isEdit) {
        const payload: UpdateVoucherPayload = {
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          voucher_call_to_action: data.voucherCallToAction.trim() ? data.voucherCallToAction.trim() : null,
          voucher_call_to_action_url: data.voucherCallToActionUrl.trim() ? data.voucherCallToActionUrl.trim() : null,
          voucher_presentation_config: {
            showCallToActionButtonInDetails: data.showCallToActionButtonInDetails,
            showConditionEvaluationWhenFailing: data.showConditionEvaluationWhenFailing,
            showConditionEvaluationWhenPassing: data.showConditionEvaluationWhenPassing,
          },
          voucher_breakdown_title: data.voucherBreakdownTitle.trim() ? data.voucherBreakdownTitle.trim() : null,
          voucher_amount_display_override: data.voucherAmountDisplayOverride.trim()
            ? data.voucherAmountDisplayOverride.trim()
            : null,
          voucher_rewards: normalizedRewards,
          voucher_conditions: serializeVoucherConditions(data.voucherConditions, existingVoucherConditionIds),
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
          voucher_user_expires_at,
          voucher_user_expires_in_days,
          theme: data.theme as UpdateVoucherPayload['theme'],
          product_type: data.productType as UpdateVoucherPayload['product_type'],
          case_id: data.caseId.trim() ? data.caseId.trim().toUpperCase() : null,
          reason: data.reason.trim() ? data.reason.trim() : null,
          reward_kind: data.rewardKind,
        };
        await updateMutation.mutateAsync({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload = {
          voucher_name: data.voucherName.trim(),
          brand: brand,
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          ...(data.voucherCallToAction.trim() ? { voucher_call_to_action: data.voucherCallToAction.trim() } : {}),
          ...(data.voucherCallToActionUrl.trim()
            ? { voucher_call_to_action_url: data.voucherCallToActionUrl.trim() }
            : {}),
          voucher_presentation_config: {
            showCallToActionButtonInDetails: data.showCallToActionButtonInDetails,
            showConditionEvaluationWhenFailing: data.showConditionEvaluationWhenFailing,
            showConditionEvaluationWhenPassing: data.showConditionEvaluationWhenPassing,
          },
          ...(data.voucherBreakdownTitle.trim() ? { voucher_breakdown_title: data.voucherBreakdownTitle.trim() } : {}),
          ...(data.voucherAmountDisplayOverride.trim()
            ? { voucher_amount_display_override: data.voucherAmountDisplayOverride.trim() }
            : {}),
          voucher_rewards: normalizedRewards,
          voucher_conditions: serializeVoucherConditions(data.voucherConditions, new Set()),
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
          voucher_user_expires_at: voucher_user_expires_at ?? undefined,
          voucher_user_expires_in_days: voucher_user_expires_in_days ?? undefined,
          theme: data.theme as CreateVoucherPayload['theme'],
          product_type: data.productType as CreateVoucherPayload['product_type'],
          ...(data.caseId.trim() ? { case_id: data.caseId.trim().toUpperCase() } : {}),
          ...(data.reason.trim() ? { reason: data.reason.trim() } : {}),
          reward_kind: data.rewardKind,
        };
        const createResponse = await createMutation.mutateAsync(payload);
        savedVoucherId = createResponse?.result?.voucherId || '';
      }

      if (voucherUsers.length > 0 && savedVoucherId) {
        const resolvedExpiredAt = expiredAt.trim()
          ? normalizeToISODate(expiredAt)
          : normalizeToISODate(
              getDefaultAddVoucherUsersExpiryDate({
                voucherUserExpiresAt: voucherUserExpiryMode === 'date' ? data.voucherUserExpiresAt : '',
                voucherUserExpiresInDays: voucherUserExpiryMode === 'days' ? data.voucherUserExpiresInDays : '',
              }),
            );
        await createVoucherUsersMutation.mutateAsync({
          voucher_id: savedVoucherId,
          expired_at: resolvedExpiredAt,
          voucher_users: voucherUsers.map((userId) => ({ user_id: userId })),
        });
      }

      reset(data);
      if (isEdit) {
        initialInternalFieldsRef.current = {
          caseId: data.caseId.trim(),
          reason: data.reason.trim(),
        };
      }
      enqueueSnackbar(isEdit ? 'Updated voucher successfully' : 'Created voucher successfully', {
        variant: 'success',
      });

      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (isEdit && voucherId) {
        queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
      }

      if (!isEdit && savedVoucherId) {
        bypassUnsavedNavigationBlockRef.current = true;
        replace(
          { pathname: `/marketing/vouchers/${savedVoucherId}` },
          { fromVoucherCreate: { voucherName: data.voucherName.trim() } },
        );
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save voucher: ${formatVoucherSaveFailure(error)}`, { variant: 'error' });
    }
  };

  const handleSaveVoucher = handleSubmit(onSubmit);

  const handleCancelDelete = () => {
    if (!deleteVoucherMutation.isPending) {
      setDeleteDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!voucherId) {
      enqueueSnackbar('Unable to delete voucher: missing voucher id.', { variant: 'error' });
      return;
    }
    try {
      await deleteVoucherMutation.mutateAsync({ voucherId });
      enqueueSnackbar(`Deleted voucher ${voucherNameValue || voucherId}.`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      queryClient.removeQueries({ queryKey: ['voucher', voucherId] });
      setDeleteDialogOpen(false);
      bypassUnsavedNavigationBlockRef.current = true;
      push('/marketing/vouchers');
    } catch (error) {
      enqueueSnackbar(`Failed to delete voucher: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  const fromCreate = (location.state as VoucherFormLocationState)?.fromVoucherCreate;
  const createdVoucherName = fromCreate?.voucherName?.trim();
  const renderSectionUnsavedChangesAlert = (hasUnsavedChanges: boolean) => {
    if (!hasUnsavedChanges) {
      return null;
    }

    return (
      <Alert
        severity="info"
        sx={{ mb: 2, alignItems: 'center' }}
        action={
          <Button color="inherit" size="small" onClick={handleSaveVoucher} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        }
      >
        Unsaved changes
      </Alert>
    );
  };

  return (
    <Container maxWidth="xl">
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" flexWrap="wrap" spacing={2} useFlexGap>
          <VoucherFormNavigation
            isSubmitting={isSubmitting}
            hasUnsavedChanges={isDirty}
            isEdit={isEdit}
            showAdvanceDescription={showAdvanceDescription}
            onBack={() => push('/marketing/vouchers')}
            onSubmit={handleSaveVoucher}
            onToggleAdvancedDescription={() => setShowAdvanceDescription(!showAdvanceDescription)}
            onOpenClone={isEdit && voucherId ? () => setCloneDialogOpen(true) : undefined}
          />
          {isEdit && voucher && (voucherAuditFields.createdByUserId || voucherAuditFields.modifiedByUserId) ? (
            <Stack alignItems="flex-end" spacing={0.25} sx={{ textAlign: 'right' }}>
              {voucherAuditFields.createdByUserId ? (
                <Typography variant="caption" color="text.secondary" component="div">
                  Created by{' '}
                  <Link to={`/users/${voucherAuditFields.createdByUserId}`}>
                    {voucherAuditUsersById.get(voucherAuditFields.createdByUserId)?.full_name ??
                      voucherAuditFields.createdByUserId}
                  </Link>
                  {voucherAuditFields.createdAt ? (
                    <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                      ({dayjs(voucherAuditFields.createdAt).format('D MMM YYYY, h:mm a')})
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
              {voucherAuditFields.modifiedByUserId ? (
                <Typography variant="caption" color="text.secondary" component="div">
                  Last modified by{' '}
                  <Link to={`/users/${voucherAuditFields.modifiedByUserId}`}>
                    {voucherAuditUsersById.get(voucherAuditFields.modifiedByUserId)?.full_name ??
                      voucherAuditFields.modifiedByUserId}
                  </Link>
                  {voucherAuditFields.updatedAt ? (
                    <Box component="span" sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                      ({dayjs(voucherAuditFields.updatedAt).format('D MMM YYYY, h:mm a')})
                    </Box>
                  ) : null}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </PageHeader>

      {isEdit && voucherId ? (
        <Box sx={{ mb: 2 }}>
          <AuditEventSummary title={voucherNameValue || voucherId} modelType="voucher" modelId={voucherId} />
        </Box>
      ) : null}

      {isEdit && voucherId && fromCreate ? (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          onClose={() => {
            const state = { ...((location.state as Record<string, unknown> | null) ?? {}) };
            delete state.fromVoucherCreate;
            replace(
              { hash: location.hash, pathname: location.pathname, search: location.search },
              Object.keys(state).length > 0 ? state : null,
            );
          }}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" component="div">
            Voucher <strong>{createdVoucherName || voucherNameValue || voucherId}</strong> was created.{' '}
            <Link to={`/marketing/vouchers/${voucherId}`}>Edit voucher</Link>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {voucherId}
          </Typography>
        </Alert>
      ) : null}

      {parentVoucherId ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Parent voucher:{' '}
            <Link to={`/marketing/vouchers/${parentVoucherId}`}>{parentVoucherName || parentVoucherId}</Link>
          </Typography>
        </Box>
      ) : null}

      {isEdit && voucherId && (
        <VoucherCloneDialog
          open={cloneDialogOpen}
          onClose={() => setCloneDialogOpen(false)}
          sourceVoucherId={voucherId}
          sourceVoucherDisplayName={voucherNameValue.trim() || undefined}
          sourceVoucherRewards={voucherRewards}
          sourceVoucherCurrencyCode={currencyCode}
          suggestedNewVoucherName={buildSuggestedClonedVoucherName(voucherNameValue)}
          clonedFromVoucherId={voucher?.clonedFromId}
          onCloned={(newVoucherId) => {
            queryClient.invalidateQueries({ queryKey: ['voucher', newVoucherId] });
            push(`/marketing/vouchers/${newVoucherId}`);
          }}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete voucher {voucherNameValue || voucherId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCancelDelete} disabled={deleteVoucherMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmDelete}
            disabled={deleteVoucherMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Stack spacing={10}>
        {voucherWarnings.length > 0 && (
          <Stack direction="column" spacing={2}>
            {voucherWarnings.map((warning, index) => (
              <Alert
                key={`${warning.code ?? 'voucher-warning'}-${index}`}
                severity={getVoucherWarningSeverity(warning.level)}
                title={JSON.stringify(warning, null, 4)}
              >
                <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                {warning.message || 'Please review this warning before proceeding.'}
              </Alert>
            ))}
          </Stack>
        )}
        <Stack spacing={2}>
          <PageSubheader title="Presentation" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Controller
              name="voucherName"
              control={control}
              rules={{
                validate: (value) => (isEdit ? true : validateVoucherName(value)),
              }}
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...field}
                    label="Voucher Name"
                    error={!!fieldState.error}
                    sx={{
                      ...(isEdit
                        ? {
                            '& .MuiInputBase-input': {
                              color: 'text.disabled',
                              WebkitTextFillColor: 'currentColor',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'text.secondary',
                            },
                          }
                        : {}),
                    }}
                    helperText={
                      fieldState.error?.message ||
                      (showAdvanceDescription
                        ? 'Name displayed for the voucher. This is used during processing and is a fallback value if the voucher title is not set'
                        : !isEdit
                          ? `Voucher name cannot be changed after creation. ${VOUCHER_NAME_PATTERN_MESSAGE}.`
                          : '')
                    }
                    fullWidth
                    required
                    disabled={isSubmitting}
                    slotProps={{
                      input: {
                        readOnly: isEdit,
                      },
                    }}
                  />
                );
              }}
            />
          </Stack>
          <Controller
            name="voucherTitle"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Title"
                helperText={
                  showAdvanceDescription
                    ? 'Optional title displayed for this voucher to the user when available.'
                    : undefined
                }
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="voucherDescription"
            control={control}
            render={({ field }) => (
              <Accordion
                expanded={descriptionAccordionExpanded}
                onChange={(_, nextExpanded) => setDescriptionAccordionExpanded(nextExpanded)}
                disableGutters
                variant="outlined"
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ overflow: 'hidden', pr: 1, width: '100%' }}>
                    <Typography component="span" fontWeight={500}>
                      Voucher Description
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {voucherDescriptionSummaryPreview}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <MarkdownEditor
                    key={descriptionEditorKey}
                    {...field}
                    helperText={
                      showAdvanceDescription
                        ? 'Optional description shown to the user to explain voucher details. Supports markdown.'
                        : undefined
                    }
                    disabled={isSubmitting}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          />
          {isEdit ? (
            <VoucherTextReplacements
              textReplacements={previewTextReplacements}
              tableAriaLabel="Voucher form text replacements"
            />
          ) : null}
          <Controller
            name="voucherCallToAction"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Call To Action"
                helperText="Optional. Overrides the default call-to-action text shown with the voucher before redemption."
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="voucherCallToActionUrl"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Call To Action URL"
                type="url"
                helperText="Optional. Destination link for the voucher call-to-action."
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="voucherBreakdownTitle"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Breakdown Title"
                helperText='Optional. Names this voucher on the checkout price breakdown row instead of the default "Voucher discount". Keep it to a few words, no amount. Leave blank for the default, which stays translated for non-English customers.'
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="voucherAmountDisplayOverride"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Amount Display Override"
                helperText={
                  showAdvanceDescription
                    ? 'Optional amount label shown to customers instead of the computed value. Use [none] to show no amount label.'
                    : 'Optional customer-facing amount label override. Use [none] to hide it.'
                }
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Accordion
            expanded={previewAccordionExpanded}
            onChange={(_, nextExpanded) => setPreviewAccordionExpanded(nextExpanded)}
            disableGutters
            variant="outlined"
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ width: '100%' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Typography component="span" fontWeight={500}>
                    Voucher preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isPreviewPending
                      ? 'Loading preview'
                      : isPreviewError || previewLocalError
                        ? 'Preview needs attention'
                        : publicPreviewVoucher
                          ? `Showing ${previewCurrencyCode}`
                          : 'No preview loaded'}
                  </Typography>
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                  <RadioGroup
                    aria-label="Preview currency"
                    value={previewCurrencyCode}
                    onChange={(_, currency) => setPreviewCurrencyCode(currency)}
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      flexWrap: 'wrap',
                      gap: 1,
                    }}
                  >
                    {resolvedVoucherPreviewCurrencyOptions.map((currency) => (
                      <FormControlLabel
                        key={currency}
                        value={currency}
                        label={currency}
                        control={<Radio disabled={isPreviewPending} />}
                        sx={{ mr: 1 }}
                      />
                    ))}
                  </RadioGroup>
                  <Button variant="text" onClick={() => handleRefreshPreview()} disabled={isPreviewPending}>
                    Refresh preview
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Best-effort mock display; may differ from the customer portal.
                  </Typography>
                </Stack>
                <VoucherPreviewContent
                  localError={previewLocalError}
                  isError={isPreviewError}
                  errorHeadline={previewErrorHeadline}
                  errorDetailBlock={previewErrorDetailBlock}
                  publicVoucher={publicPreviewVoucher}
                  textReplacements={previewTextReplacements}
                  isPending={isPreviewPending}
                  emptyMessage="The preview will load when the required voucher fields are available."
                  loadingMessage="Refreshing preview..."
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded={false} disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography component="span" fontWeight={500}>
                  Advanced voucher presentation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Control customer-facing voucher details and condition evaluation.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Stack spacing={1}>
                <Controller
                  name="showCallToActionButtonInDetails"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Show the call-to-action button in voucher details"
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          disabled={isSubmitting}
                        />
                      }
                    />
                  )}
                />
                <Controller
                  name="showConditionEvaluationWhenFailing"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Show condition evaluation when the voucher cannot be used"
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          disabled={isSubmitting}
                        />
                      }
                    />
                  )}
                />
                <Controller
                  name="showConditionEvaluationWhenPassing"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Show condition evaluation when the voucher can be used"
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(_, checked) => field.onChange(checked)}
                          disabled={isSubmitting}
                        />
                      }
                    />
                  )}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
          <PageSubheader title="Internal reference" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reason"
                  fullWidth
                  required={!isEdit}
                  disabled={isSubmitting}
                  helperText={
                    showAdvanceDescription
                      ? 'Internal note describing why this voucher exists (not shown to customers).'
                      : 'Internal note (not shown to customers).'
                  }
                />
              )}
            />
            <Controller
              name="caseId"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Case ID"
                  fullWidth
                  disabled={isSubmitting}
                  helperText={
                    showAdvanceDescription
                      ? 'Internal CaseId (internal only).'
                      : 'Internal only; stored uppercased. (internal only).'
                  }
                />
              )}
            />
          </Stack>
          <Controller
            name="labAudienceId"
            control={control}
            render={({ field }) => {
              const selected =
                field.value == null
                  ? null
                  : (labAudienceOptions.find((option) => option.id === field.value) ?? {
                      id: field.value,
                      name: `Audience #${field.value} (unavailable)`,
                    });
              return (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                  <Autocomplete
                    options={labAudienceOptions}
                    value={selected}
                    onChange={(_, next) => field.onChange(next?.id ?? null)}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        setLabAudienceSearch(value);
                      }
                    }}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, val) => option.id === val.id}
                    filterOptions={(options) => options}
                    loading={isLabAudiencesFetching}
                    disabled={isSubmitting}
                    fullWidth
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Lab audience"
                        helperText="Not saved on the voucher — used only for Sync to Lab"
                      />
                    )}
                  />
                  {isEdit && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={field.value == null || syncLabAudienceMutation.isPending || isSubmitting || !voucherId}
                      onClick={() => {
                        if (voucherId && field.value != null) {
                          syncLabAudienceMutation.mutate({ id: voucherId, labAudienceId: field.value });
                        }
                      }}
                      sx={{ mt: { sm: 1 }, whiteSpace: 'nowrap' }}
                    >
                      {syncLabAudienceMutation.isPending ? 'Syncing…' : 'Sync to Lab'}
                    </Button>
                  )}
                </Stack>
              );
            }}
          />
          {!isPromoMetaLoading && promoMeta && promoMeta.voucher_toggles.length > 0 && (
            <Controller
              name="voucherToggles"
              control={control}
              render={({ field }) => (
                <Box>
                  <SummaryToggleAccordion
                    title="Voucher Toggles"
                    options={voucherToggleOptionsWithUnknown}
                    selectedKeys={field.value ?? []}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                </Box>
              )}
            />
          )}
          <Controller
            name="showVoucherFromDate"
            control={control}
            render={({ field }) => {
              const v = field.value || '';
              const inputValue = showVoucherFromDateIncludeTime
                ? v
                  ? v.length === 10
                    ? `${v}T00:00`
                    : v.slice(0, 16)
                  : ''
                : v
                  ? v.slice(0, 10)
                  : '';
              return (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    label="Show Voucher From Date"
                    type={showVoucherFromDateIncludeTime ? 'datetime-local' : 'date'}
                    value={inputValue}
                    onChange={(event) => field.onChange(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={showVoucherFromDateIncludeTime ? { step: 60 } : undefined}
                    helperText={
                      showAdvanceDescription
                        ? showVoucherFromDateIncludeTime
                          ? 'Optional date and time (Sydney time) from which this voucher becomes visible to users. Leave empty for immediate visibility.'
                          : 'Optional: first date this voucher is visible, from midnight Sydney time. Most of the time the start time does not matter—use Set time only when you need a specific time. Leave empty for immediate visibility.'
                        : undefined
                    }
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showVoucherFromDateIncludeTime}
                        onChange={(_, checked) => {
                          setShowVoucherFromDateIncludeTime(checked);
                          const current = (getValues('showVoucherFromDate') || '').trim();
                          if (checked) {
                            // Empty means "immediate visibility" — do not invent today's date at midnight.
                            if (!current) {
                              field.onChange('');
                              return;
                            }
                            const datePart = current.length >= 10 ? current.slice(0, 10) : dayjs().format('YYYY-MM-DD');
                            field.onChange(`${datePart}T00:00`);
                          } else {
                            const datePart =
                              current.length >= 16 ? current.slice(0, 10) : current.length === 10 ? current : '';
                            field.onChange(datePart);
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    }
                    label="Set time"
                    sx={{
                      m: 0,
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      alignSelf: 'center',
                      pr: 0.5,
                    }}
                  />
                </Box>
              );
            }}
          />
          <Stack spacing={1}>
            <Typography variant="subtitle2">Voucher User Expiry</Typography>
            {showAdvanceDescription
              ? 'These dates control what date is used when a voucher user for this voucher is created. Its is also used to determine the "liability" associated with a voucher.'
              : undefined}
            <RadioGroup
              row
              value={voucherUserExpiryMode}
              onChange={(e) => {
                const next = e.target.value as VoucherUserExpiryMode;
                voucherUserExpiryModeRef.current = next;
                setVoucherUserExpiryMode(next);
                if (next === 'days') {
                  setValue('voucherUserExpiresAt', '', { shouldDirty: true, shouldValidate: true });
                } else {
                  setValue('voucherUserExpiresInDays', '', { shouldDirty: true, shouldValidate: true });
                }
                void trigger(['voucherUserExpiresAt', 'voucherUserExpiresInDays']);
              }}
            >
              <FormControlLabel value="days" control={<Radio />} label="Expiry Days" disabled={isSubmitting} />
              <FormControlLabel value="date" control={<Radio />} label="Expiry Date" disabled={isSubmitting} />
            </RadioGroup>
            {voucherUserExpiryMode === 'date' && (
              <Controller
                name="voucherUserExpiresAt"
                control={control}
                rules={{
                  validate: (value) => {
                    if (voucherUserExpiryModeRef.current !== 'date') {
                      return true;
                    }
                    const at = (value || '').trim();
                    if (!at) {
                      return 'Enter a voucher user expiry date, or switch to expiry days from assignment.';
                    }
                    const showFromRaw = (getValues('showVoucherFromDate') || '').trim();
                    if (showFromRaw) {
                      const startDatePart = showFromRaw.length >= 10 ? showFromRaw.slice(0, 10) : showFromRaw;
                      const expiryDatePart = at.length >= 10 ? at.slice(0, 10) : at;
                      if (dayjs(startDatePart).isAfter(dayjs(expiryDatePart), 'day')) {
                        return 'Expiry date must be on or after the voucher start date.';
                      }
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Voucher user expires at"
                    required
                    type="date"
                    value={field.value ? dayjs(field.value).format('YYYY-MM-DD') : ''}
                    onChange={(event) => {
                      const localValue = event.target.value;
                      if (!localValue) {
                        field.onChange('');
                        return;
                      }
                      field.onChange(dayjs(localValue).format('YYYY-MM-DD'));
                    }}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ||
                      (showAdvanceDescription
                        ? 'Fixed expiry for issued voucher users, from midnight Sydney time (relative expiry uses Expiry Days).'
                        : undefined)
                    }
                    fullWidth
                    disabled={isSubmitting}
                    sx={highlightVoucherUserExpiryFields ? voucherUserExpiryWarningFieldSx : undefined}
                  />
                )}
              />
            )}
            {voucherUserExpiryMode === 'days' && (
              <Controller
                name="voucherUserExpiresInDays"
                control={control}
                rules={{
                  validate: (value) => {
                    if (voucherUserExpiryModeRef.current !== 'days') {
                      return true;
                    }
                    const trimmed = (value || '').trim();
                    if (!trimmed) {
                      return 'Enter expiry days from assignment, or switch to a fixed expiry date.';
                    }
                    const parsed = Number.parseInt(trimmed, 10);
                    if (Number.isNaN(parsed)) {
                      return 'Enter a valid whole number';
                    }
                    if (parsed < 0) {
                      return 'Must be 0 or greater';
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Voucher user expires in (days)"
                    required
                    type="number"
                    error={Boolean(fieldState.error)}
                    inputProps={{ min: 0 }}
                    helperText={
                      fieldState.error?.message ||
                      (showAdvanceDescription
                        ? 'Days from issue the voucher remains usable (fixed calendar expiry uses Expiry Date).'
                        : undefined)
                    }
                    fullWidth
                    disabled={isSubmitting}
                    sx={highlightVoucherUserExpiryFields ? voucherUserExpiryWarningFieldSx : undefined}
                  />
                )}
              />
            )}
            <VoucherLongExpiryPromoAdminAlert
              isCreate={!isEdit}
              mode={voucherUserExpiryMode}
              voucherUserExpiresAt={watchedVoucherUserExpiresAt ?? ''}
              voucherUserExpiresInDays={watchedVoucherUserExpiresInDays ?? ''}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="theme"
              control={control}
              render={({ field }) => {
                const row = voucherThemeMenuRows.find((r) => r.key === field.value);
                const descriptionHint =
                  showAdvanceDescription &&
                  (row?.description?.trim() || 'Optional theme for the voucher display styling.');
                return (
                  <TextField
                    {...field}
                    select
                    label="Theme"
                    helperText={descriptionHint || undefined}
                    fullWidth
                    disabled={isSubmitting || (isPromoMetaLoading && voucherThemeMenuRows.length === 0)}
                  >
                    {voucherThemeMenuRows.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
            <Controller
              name="productType"
              control={control}
              render={({ field }) => {
                const row = voucherProductTypeMenuRows.find((r) => r.key === field.value);
                const descriptionHint =
                  showAdvanceDescription &&
                  (row?.description?.trim() || 'Which product type this voucher aligns with (for presentation).');
                return (
                  <TextField
                    {...field}
                    select
                    label="Product Type"
                    helperText={descriptionHint || undefined}
                    fullWidth
                    disabled={isSubmitting || (isPromoMetaLoading && voucherProductTypeMenuRows.length === 0)}
                  >
                    {voucherProductTypeMenuRows.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
          </Stack>
        </Stack>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Voucher logic</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack direction="column" gap={1.5}>
              <Stack direction="column" gap={0.5}>
                <Typography variant="subtitle2">Voucher discount calculation</Typography>
                <Typography variant="caption">
                  Checkout runs /discount with the current cart/order payload, user, request source, and selected
                  voucher ids. The request and response are saved for the Promo Requests admin view, including voucher
                  names, offer ids, amount, and the first error code/message when the calculation fails.
                </Typography>
                <Typography variant="caption">
                  Each reward row below is evaluated against the order. Products define which items can receive voucher
                  value; Min Spend Products define which items are counted against the reward's Min Spend value.
                </Typography>
                <Typography variant="caption">
                  If multiple reward rows qualify, the calculator chooses the best eligible reward for the voucher,
                  accounts for any partial redemption amount already used, and returns the voucher amount, display
                  voucher, voucher totals, credit totals, and item-level vouchersApplied data for checkout/payment.
                </Typography>
                <Typography variant="caption">
                  Payment logging runs /vouchers/log after the order is placed. It revalidates the submitted
                  vouchersApplied payload, marks voucher users as used or increments amount used, and writes Voucher
                  Logs for admin visibility.
                </Typography>
              </Stack>

              <Stack direction="column" gap={0.5}>
                <Typography variant="subtitle2">Condition details</Typography>
                <Typography variant="caption">
                  Voucher-wide validation checks voucher existence, brand, stacking rules, promo-stacking rules, account
                  country/currency guards, used state, voucher-user ownership, currency, reward amount, and remaining
                  balance.
                </Typography>
                <Typography variant="caption">
                  Voucher conditions can filter specific reward rows when their Reward filter is set. If all rewards are
                  filtered out, or a condition is configured to disallow the whole voucher, the voucher cannot be used.
                </Typography>
                <Typography variant="caption">
                  Product, min spend, destination, occupancy, booking date, margin, provider, fare, flight, place id,
                  subscriber tier, loyalty offer type, friends-and-family, and spoofing conditions can remove matching
                  rewards or block the voucher, depending on their configuration.
                </Typography>
                <Typography variant="caption">
                  Provide-credit rewards are calculated during /discount and rechecked during /vouchers/log for credit
                  amount, payout date, item end dates, and currency before the voucher log is persisted.
                </Typography>
              </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <Box mt={3}>
          <PageSubheader title="Rewards">
            {showAdvanceDescription && (
              <Box component="span" color="text.secondary" alignItems="left">
                The amount the voucher is worth, and for which products. Only one reward is applied to an order.
              </Box>
            )}
          </PageSubheader>
          {renderSectionUnsavedChangesAlert(hasUnsavedRewardChanges)}
          <Box sx={{ mb: 2, maxWidth: { sm: 480 } }}>
            <Controller
              name="rewardKind"
              control={control}
              render={({ field }) => {
                const helperPrimary = showAdvanceDescription
                  ? 'Reward kind options (key, name, description, admin visibility) come from promo meta.'
                  : undefined;
                return (
                  <TextField
                    {...field}
                    select
                    label="Reward kind"
                    helperText={helperPrimary || undefined}
                    fullWidth
                    disabled={isSubmitting || (isPromoMetaLoading && voucherRewardKindMenuRows.length === 0)}
                  >
                    {voucherRewardKindMenuRows.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        <ListItemText
                          primary={option.name}
                          secondary={option.description?.trim() || undefined}
                          slotProps={{
                            secondary: {
                              sx: { whiteSpace: 'normal', typography: 'caption', display: 'block', mt: 0.25 },
                            },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
          </Box>
          <Controller
            name="voucherRewards"
            control={control}
            render={({ field }) => (
              <VoucherRewards
                rewards={field.value}
                rewardKind={watchedRewardKind}
                conditions={voucherConditions}
                onChange={field.onChange}
                currencyCode={currencyCode}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
        <Box mt={3}>
          <PageSubheader title="Conditions" />
          {renderSectionUnsavedChangesAlert(hasUnsavedConditionChanges)}
          <Controller
            name="voucherConditions"
            control={control}
            render={({ field }) => (
              <VoucherConditions
                conditions={field.value}
                rewards={voucherRewards}
                onChange={field.onChange}
                showAdvanceDescription={showAdvanceDescription}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
        <VoucherUsers
          userIds={voucherUsers}
          onChange={setVoucherUsers}
          expiredAt={expiredAt}
          onExpiredAtChange={setExpiredAt}
          voucherUserExpiresAt={watchedVoucherUserExpiresAt ?? ''}
          voucherUserExpiresInDays={watchedVoucherUserExpiresInDays ?? ''}
          showAdvanceDescription={showAdvanceDescription}
          disabled={isSubmitting}
          voucherId={voucherId}
          voucherName={voucherNameValue ?? ''}
          promoRequestsUserFilter={promoRequestsUserFilter}
          onPromoRequestsUserFilterChange={setPromoRequestsUserFilter}
        />
        {isEdit && voucherNameValue && brand && (
          <VoucherRecentRequests
            voucherName={voucherNameValue}
            userId={promoRequestsUserFilter}
            brand={brand}
            promoMeta={promoMeta}
            defaultVisibleItemFields={defaultVisibleRequestItemFields}
            showVoucherLogsAccordion={false}
          />
        )}
        <Box mt={3}>
          <PageSubheader title="Debug" />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {isEdit && <DebugModal type="generic" title="Voucher API Response" data={voucherResponse?.result} />}
            <DebugModal type="generic" title="Promo Meta" data={promoMeta} />
          </Stack>
        </Box>
        {isEdit && voucherNameValue?.trim() && (
          <Box mt={3}>
            <VoucherCheck voucherName={voucherNameValue.trim()} />
          </Box>
        )}
        {isEdit && voucherId && (
          <Box mt={2}>
            <Divider />
            <Box mt={2}>
              <Button
                variant="text"
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteVoucherMutation.isPending || isSubmitting}
              >
                Delete voucher
              </Button>
            </Box>
          </Box>
        )}
      </Stack>
    </Container>
  );
}
